import type { RouteRecordRaw } from 'vue-router'
import { ROLE_GROUPS } from '../../constants/roleGroups'

const adminRoleMeta = {
  requiresAuth: true,
  requireRoles: ROLE_GROUPS.ADMIN_PANEL,
}

export const adminBoraLearnRoutes: RouteRecordRaw[] = [
  {
    path: 'boralearn/students',
    name: 'admin.boralearn.students',
    component: () => import('../../views/admin/users/StudentsListView.vue'),
    meta: {
      ...adminRoleMeta,
      title: 'BORAlearn Students',
      requireRoles: ROLE_GROUPS.ADMINS,
    },
  },
  {
    path: 'boralearn/students/:id/badges',
    name: 'admin.boralearn.students.badges',
    component: () => import('../../views/admin/boralearn/StudentBadgesView.vue'),
    meta: {
      ...adminRoleMeta,
      title: 'Student Badges',
      requireRoles: ROLE_GROUPS.ADMINS,
    },
  },
  {
    path: 'boralearn/students/:id',
    name: 'admin.boralearn.students.detail',
    component: () => import('../../views/admin/users/StudentDetailView.vue'),
    meta: {
      ...adminRoleMeta,
      title: 'BORAlearn Student Detail',
      requireRoles: ROLE_GROUPS.ADMINS,
    },
  },
  {
    path: 'boralearn/teachers',
    name: 'admin.boralearn.teachers',
    component: () => import('../../views/admin/users/TeachersListView.vue'),
    meta: { ...adminRoleMeta, title: 'BORAlearn Teachers', requireRoles: ROLE_GROUPS.ADMINS },
  },
  {
    path: 'boralearn/teachers/:id',
    name: 'admin.boralearn.teachers.detail',
    component: () => import('../../views/admin/users/TeacherDetailView.vue'),
    meta: { ...adminRoleMeta, title: 'BORAlearn Teacher Detail', requireRoles: ROLE_GROUPS.ADMINS },
  },
  {
    path: 'boralearn/schools',
    name: 'admin.boralearn.schools',
    component: () => import('../../views/admin/users/SchoolsListView.vue'),
    meta: { ...adminRoleMeta, title: 'BORAlearn Schools', requireRoles: ROLE_GROUPS.ADMINS },
  },
  {
    path: 'boralearn/schools/:id',
    name: 'admin.boralearn.schools.detail',
    component: () => import('../../views/admin/users/SchoolDetailView.vue'),
    meta: { ...adminRoleMeta, title: 'BORAlearn School Detail', requireRoles: ROLE_GROUPS.ADMINS },
  },
  {
    path: 'boralearn/parents',
    name: 'admin.boralearn.parents',
    component: () => import('../../views/admin/users/ParentsListView.vue'),
    meta: { ...adminRoleMeta, title: 'BORAlearn Parents' },
  },
  {
    path: 'boralearn/parents/:id',
    name: 'admin.boralearn.parents.detail',
    component: () => import('../../views/admin/users/ParentDetailView.vue'),
    meta: { ...adminRoleMeta, title: 'BORAlearn Parent Detail' },
  },
  {
    path: 'boralearn/parent-requests',
    name: 'admin.boralearn.parent-requests',
    component: () => import('../../views/admin/users/ParentRequestsView.vue'),
    meta: { ...adminRoleMeta, title: 'Parent/Learner Requests' },
  },
  {
    path: 'boralearn/question-bank',
    name: 'admin.boralearn.question-bank',
    redirect: { name: 'admin.boralearn.question-bank.list' },
    meta: {
      ...adminRoleMeta,
      title: 'BORAlearn Question Bank',
      requiredPermission: 'admin.learn.view',
    },
  },
  {
    path: 'boralearn/question-bank/questions',
    name: 'admin.boralearn.question-bank.list',
    component: () => import('../../views/boralearn/question-bank/QuestionListView.vue'),
    meta: {
      ...adminRoleMeta,
      title: 'BORAlearn Question Bank - Questions',
      requiredPermission: 'admin.learn.view',
    },
  },
  {
    path: 'boralearn/question-bank/questions/create',
    name: 'admin.boralearn.question-bank.create',
    component: () => import('../../views/boralearn/question-bank/AddQuestionWizardView.vue'),
    meta: {
      ...adminRoleMeta,
      title: 'BORAlearn Question Bank - Add Question',
      requiredPermission: 'admin.learn.manage',
    },
  },
  {
    path: 'boralearn/question-bank/import',
    name: 'admin.boralearn.question-bank.import',
    component: () => import('../../views/boralearn/question-bank/BulkImportQuestionsView.vue'),
    meta: {
      ...adminRoleMeta,
      title: 'BORAlearn Question Bank - Bulk Import',
      requiredPermission: 'admin.learn.manage',
    },
  },
  {
    path: 'boralearn/question-bank/questions/:id',
    name: 'admin.boralearn.question-bank.detail',
    component: () => import('../../views/boralearn/question-bank/QuestionDetailView.vue'),
    meta: {
      ...adminRoleMeta,
      title: 'BORAlearn Question Bank - Question Detail',
      requiredPermission: 'admin.learn.view',
    },
  },
  {
    path: 'boralearn/question-bank/questions/:id/edit',
    name: 'admin.boralearn.question-bank.edit',
    component: () => import('../../views/boralearn/question-bank/EditQuestionView.vue'),
    meta: {
      ...adminRoleMeta,
      title: 'BORAlearn Question Bank - Edit Question',
      requiredPermission: 'admin.learn.manage',
    },
  },
  {
    path: 'boralearn/question-bank/questions/:id/answers',
    name: 'admin.boralearn.question-bank.answers',
    component: () => import('../../views/boralearn/question-bank/QuestionAnswersView.vue'),
    meta: {
      ...adminRoleMeta,
      title: 'BORAlearn Question Bank - Answers',
      requiredPermission: 'admin.learn.manage',
    },
  },
  {
    path: 'boralearn/badges',
    name: 'admin.boralearn.badges',
    component: () => import('../../views/admin/boralearn/BadgesView.vue'),
    meta: { ...adminRoleMeta, title: 'BORAlearn Badges & Ranks' },
  },
  {
    path: 'boralearn/badges/:badgeCode',
    name: 'admin.boralearn.badges.earners',
    component: () => import('../../views/admin/boralearn/BadgeEarnersView.vue'),
    meta: { ...adminRoleMeta, title: 'BORAlearn Badge Earners' },
  },
  {
    path: 'boralearn/lessons',
    name: 'admin.boralearn.lessons',
    component: () => import('../../views/boralearn/BoraLearnLessonsView.vue'),
    meta: { ...adminRoleMeta, title: 'BORAlearn Lessons' },
  },
  {
    path: 'boralearn/settings',
    name: 'admin.boralearn.settings',
    component: () => import('../../views/boralearn/settings/BoraLearnSettingsView.vue'),
    redirect: { name: 'admin.boralearn.settings.academic' },
    meta: { ...adminRoleMeta, title: 'BORAlearn Settings' },
    children: [
      {
        path: 'academic',
        name: 'admin.boralearn.settings.academic',
        component: () => import('../../views/boralearn/settings/AcademicSettingsView.vue'),
        meta: { ...adminRoleMeta, title: 'BORAlearn Settings - Academic' },
      },
      {
        path: 'questions',
        name: 'admin.boralearn.settings.questions',
        component: () => import('../../views/boralearn/settings/QuestionSettingsView.vue'),
        meta: { ...adminRoleMeta, title: 'BORAlearn Settings - Questions' },
      },
      {
        path: 'lessons',
        name: 'admin.boralearn.settings.lessons',
        component: () => import('../../views/boralearn/settings/LessonSettingsView.vue'),
        meta: { ...adminRoleMeta, title: 'BORAlearn Settings - Lessons' },
      },
      {
        path: 'past-papers',
        name: 'admin.boralearn.settings.past-papers',
        component: () => import('../../views/boralearn/settings/PastPaperSettingsView.vue'),
        meta: { ...adminRoleMeta, title: 'BORAlearn Settings - Past Papers' },
      },
      {
        path: 'past-papers/bulk-upload',
        name: 'admin.boralearn.settings.past-papers.bulk-upload',
        component: () => import('../../views/boralearn/settings/PastPaperBulkUploadView.vue'),
        meta: { ...adminRoleMeta, title: 'BORAlearn Settings - Past Paper Bulk Upload' },
      },
      {
        path: 'assessments',
        name: 'admin.boralearn.settings.assessments',
        component: () => import('../../views/boralearn/settings/AssessmentSettingsView.vue'),
        meta: { ...adminRoleMeta, title: 'BORAlearn Settings - Assessments' },
      },
      {
        path: 'curricula',
        name: 'admin.boralearn.settings.curricula',
        component: () => import('../../views/admin/boralearn/settings/CurriculumSettingsView.vue'),
        meta: { ...adminRoleMeta, title: 'BORAlearn Settings - Curriculum' },
      },
    ],
  },
]
